import React, { useEffect, useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { get } from 'es-toolkit/compat'
import { LineChart } from '@mui/x-charts/LineChart'
import {
  Box,
  Paper,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { styled } from '@mui/material/styles'
import { elevationProfileClr } from 'actions/map'
import { useTranslation } from 'react-i18next'
import Draggable from 'react-draggable'
import { ResizableBox } from 'react-resizable'
import { unitConvert } from '@farmtrx/units'
import 'react-resizable/css/styles.css'
import { trackEvent } from 'api/usage-tracking'

const ChartContainer = styled(Paper)`
  width: 100%;
  height: 100%;
  padding: 8px 12px;
  background: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
`

const ResizableWrapper = styled('div')`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;

  .react-resizable {
    position: relative;
  }
`

const Header = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  cursor: grab;
`

export default function ElevationProfileView() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const elevationReq = useSelector((state) =>
    get(state, 'map.elevationProfileReq'),
  )
  const elevationRes = useSelector((state) =>
    get(state, 'map.elevationProfileRes'),
  )

  const prefs = useSelector((state) => get(state, 'account.singleton.prefs'))
  const lengthUnit = prefs?.units?.length_in || unitConvert.METERS

  const nodeRef = useRef(null)
  const [chartData, setChartData] = useState([])
  const [yAxisMin, setYAxisMin] = useState(0)
  const [yAxisMax, setYAxisMax] = useState(100)

  const [isResizing, setIsResizing] = useState(false)

  const handleResizeStart = () => setIsResizing(true)
  const handleResizeStop = () => setIsResizing(false)

  useEffect(() => {
    // Tracking event: only once per session
    if (!sessionStorage.getItem('umami_elevation_profile_tool_fired')) {
      trackEvent('elevation-profile-tool')
      sessionStorage.setItem('umami_elevation_profile_tool_fired', '1')
    }

    // GeoServer gs:LongitudinalProfile WPS returns JSON like:
    // { "profile": [{ "totalDistanceToThisPoint": 0, "altitude": 714, "slope": 0, "y": 53, "x": -108 }, ...] }
    if (elevationRes && typeof elevationRes !== 'symbol') {
      let parsed = []
      if (elevationRes.profile && Array.isArray(elevationRes.profile)) {
        parsed = elevationRes.profile.map((p) => {
          const elev = p.altitude ?? 0
          return {
            distance: unitConvert.convertLength(
              p.totalDistanceToThisPoint ?? 0,
              lengthUnit,
              unitConvert.METERS,
            ),
            elevation:
              elev === -99999
                ? -99999
                : unitConvert.convertLength(
                    elev,
                    lengthUnit,
                    unitConvert.METERS,
                  ),
          }
        })
      } else if (
        elevationRes.features &&
        Array.isArray(elevationRes.features)
      ) {
        parsed = elevationRes.features.map((f) => {
          const elev = f.properties?.value ?? 0
          return {
            distance: unitConvert.convertLength(
              f.properties?.distance ?? 0,
              lengthUnit,
              unitConvert.METERS,
            ),
            elevation:
              elev === -99999
                ? -99999
                : unitConvert.convertLength(
                    elev,
                    lengthUnit,
                    unitConvert.METERS,
                  ),
          }
        })
      } else if (Array.isArray(elevationRes)) {
        // Fallback for direct array
        parsed = elevationRes.map((r, i) => {
          const elev = r.elevation ?? r.value ?? r.v ?? r.altitude ?? 0
          return {
            distance: unitConvert.convertLength(
              r.distance ?? r.d ?? r.totalDistanceToThisPoint ?? i,
              lengthUnit,
              unitConvert.METERS,
            ),
            elevation:
              elev === -99999
                ? -99999
                : unitConvert.convertLength(
                    elev,
                    lengthUnit,
                    unitConvert.METERS,
                  ),
          }
        })
      }

      // Convert no data (-99999) to null instead of filtering, allowing us to display gaps
      parsed = parsed.map((p) => ({
        distance: p.distance,
        elevationValid: p.elevation === -99999 ? null : p.elevation,
        elevationInterp: p.elevation === -99999 ? null : p.elevation,
      }))

      setChartData(parsed)

      if (parsed.length > 0) {
        const validElevations = parsed
          .map((p) => p.elevationValid)
          .filter((e) => e !== null)

        if (validElevations.length > 0) {
          const minElevation = Math.min(...validElevations)
          const maxElevation = Math.max(...validElevations)

          const span = maxElevation - minElevation
          const padding = span > 0 ? span * 0.1 : 1

          setYAxisMin(minElevation - padding)
          setYAxisMax(maxElevation + padding)
        } else {
          setYAxisMin(0)
          setYAxisMax(100)
        }
      } else {
        setYAxisMin(0)
        setYAxisMax(100)
      }
    } else {
      setChartData([])
      setYAxisMin(0)
      setYAxisMax(100)
    }
  }, [elevationRes])

  if (!elevationReq) return null

  const handleClose = () => {
    dispatch(elevationProfileClr())
  }

  const isLoading =
    elevationReq &&
    (elevationRes === null ||
      elevationRes === undefined ||
      typeof elevationRes === 'symbol')

  const startX = typeof window !== 'undefined' ? window.innerWidth / 2 - 300 : 0
  const startY = typeof window !== 'undefined' ? window.innerHeight - 404 : 0

  return (
    <Draggable
      handle=".drag-handle"
      nodeRef={nodeRef}
      defaultPosition={{ x: startX, y: startY }}
    >
      <ResizableWrapper ref={nodeRef}>
        <ResizableBox
          width={600}
          height={280}
          minConstraints={[400, 300]}
          maxConstraints={[1200, 800]}
          resizeHandles={['se']}
          onResizeStart={handleResizeStart}
          onResizeStop={handleResizeStop}
        >
          <ChartContainer elevation={3}>
            <Header className="drag-handle">
              <Typography variant="h6">{t('elevation_profile')}</Typography>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Header>

            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 4,
                  flex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            ) : chartData.length > 0 ? (
              <Box
                style={{ position: 'relative', width: '100%', height: '100%' }}
                sx={{
                  flex: 1,
                }}
              >
                {isResizing ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      color: 'text.disabled',
                    }}
                  >
                    <CircularProgress size={32} />
                  </Box>
                ) : (
                  <LineChart
                    xAxis={[
                      {
                        dataKey: 'distance',
                        label: `${t('distance', 'Distance')} (${t(lengthUnit)})`,
                        valueFormatter: (v) =>
                          `${Math.round(v)} ${t(lengthUnit)}`,
                      },
                    ]}
                    yAxis={[
                      {
                        min: yAxisMin,
                        max: yAxisMax,
                        valueFormatter: (v) =>
                          `${Math.round(v)} ${t(lengthUnit)}`,
                      },
                    ]}
                    series={[
                      {
                        id: 'interpolated',
                        dataKey: 'elevationInterp',
                        color: '#9e9e9e',
                        showMark: false,
                        area: false,
                        connectNulls: true,
                      },
                      {
                        id: 'real',
                        dataKey: 'elevationValid',
                        label: t('elevation', 'Elevation'),
                        color: '#3475E0',
                        showMark: false,
                        area: true,
                        connectNulls: false,
                      },
                    ]}
                    dataset={chartData}
                    margin={{ left: 50, right: 10, top: 10, bottom: 40 }}
                    sx={{
                      '& .MuiLineElement-series-interpolated': {
                        strokeDasharray: '5 5',
                      },
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                    }}
                  />
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  p: 3,
                  textAlign: 'center',
                  flex: 1,
                }}
              >
                <Typography color="textSecondary">
                  {t('no_elevation_data')}
                </Typography>
              </Box>
            )}
          </ChartContainer>
        </ResizableBox>
      </ResizableWrapper>
    </Draggable>
  )
}
